import apiClient from './apiClient';

// Types and interfaces
export interface BankDetails {
  accountHolderName: string;
  bankName: string;
  accountNumber: string; // May be masked/encrypted when received from backend
  ifscCode: string;
}

export interface AmbassadorApplication {
  _id: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  bankDetails?: BankDetails;
  totalEarnings: number;
  totalPayouts: number;
  pendingEarnings: number;
  totalReferrals: number;
  successfulReferrals: number;
  appliedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  suspendedAt?: string;
  rejectionReason?: string;
  suspensionReason?: string;
  notes?: string;
  razorpayContactId?: string;
  razorpayFundAccountId?: string;
}

export interface AmbassadorStatus {
  isAmbassador: boolean;
  status: string | null;
  application: AmbassadorApplication | null;
}

export interface AmbassadorEarnings {
  totalEarnings: number;
  pendingEarnings: number;
  totalPayouts: number;
  totalReferrals: number;
  successfulReferrals: number;
}

export interface PayoutInfo {
  id: string;
  status: string;
  amount: number;
  currency: string;
  utr: string | null;
  mode: string;
  reference_id: string;
  fees: number;
  tax: number;
  created_at: number;
  status_details?: any;
}

export interface PayoutHistoryResponse {
  payouts: PayoutInfo[];
  total: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

class AmbassadorService {
  private static instance: AmbassadorService;
  
  // Base URL for ambassador endpoints
  private readonly baseUrl = '/ambassador';

  private constructor() {}

  public static getInstance(): AmbassadorService {
    if (!AmbassadorService.instance) {
      AmbassadorService.instance = new AmbassadorService();
    }
    return AmbassadorService.instance;
  }

  /**
   * Apply for ambassador program
   */
  async applyForAmbassador(): Promise<AmbassadorApplication> {
    try {
      console.log('Applying for ambassador program...');
      
      const response = await apiClient.post<ApiResponse<AmbassadorApplication>>('/ambassador/apply', {});
      
      console.log('Ambassador application response:', response);
      return response.data;
    } catch (error: any) {
      console.error('Error applying for ambassador program:', error);
      
      // Preserve status code for authentication handling
      if (error.status) {
        const preservedError = new Error(error.message || 'Failed to apply for ambassador program');
        (preservedError as any).status = error.status;
        throw preservedError;
      }
      
      throw new Error(error.message || 'Failed to apply for ambassador program');
    }
  }

  /**
   * Get current ambassador status
   * Note: Bank details will be decrypted by backend before sending
   */
  async getAmbassadorStatus(): Promise<AmbassadorStatus> {
    try {
      console.log('Fetching ambassador status...');
      
      const response = await apiClient.get<ApiResponse<AmbassadorStatus>>(
        `${this.baseUrl}/status`
      );

      const status = response.data;
      console.log('Ambassador status retrieved:', status);
      
      return status;
    } catch (error: any) {
      console.error('Error fetching ambassador status:', error);
      
      // Preserve status code for authentication handling
      if (error.status) {
        const preservedError = new Error(error.message || 'Failed to fetch ambassador status');
        (preservedError as any).status = error.status;
        throw preservedError;
      }
      
      throw new Error(error.message || 'Failed to fetch ambassador status');
    }
  }

  /**
   * Update bank details
   * Note: Bank details will be encrypted by backend before storage
   */
  async updateBankDetails(bankDetails: BankDetails): Promise<AmbassadorApplication> {
    try {
      console.log('Updating bank details...');
      
      // Validate bank details before sending
      this.validateBankDetails(bankDetails);

      const response = await apiClient.put<ApiResponse<AmbassadorApplication>>(
        `${this.baseUrl}/bank-details`,
        bankDetails
      );

      const application = response.data;
      console.log('Successfully updated bank details:', application);
      
      return application;
    } catch (error: any) {
      console.error('Error updating bank details:', error);
      
      // Preserve status code for authentication handling
      if (error.status) {
        const preservedError = new Error(error.message || 'Failed to update bank details');
        (preservedError as any).status = error.status;
        throw preservedError;
      }
      
      throw new Error(error.message || 'Failed to update bank details');
    }
  }

  /**
   * Get ambassador earnings
   */
  async getEarnings(): Promise<AmbassadorEarnings> {
    try {
      console.log('Fetching ambassador earnings...');
      
      const response = await apiClient.get<ApiResponse<AmbassadorEarnings>>(
        `${this.baseUrl}/earnings`
      );

      const earnings = response.data;
      console.log('Ambassador earnings retrieved:', earnings);
      
      return earnings;
    } catch (error: any) {
      console.error('Error fetching ambassador earnings:', error);
      throw new Error(error.message || 'Failed to fetch earnings');
    }
  }

  /**
   * Get payout history
   */
  async getPayoutHistory(): Promise<PayoutHistoryResponse> {
    try {
      console.log('Fetching payout history...');
      
      const response = await apiClient.get<ApiResponse<PayoutHistoryResponse>>(
        `${this.baseUrl}/payouts`
      );

      const payouts = response.data;
      console.log('Payout history retrieved:', payouts);
      
      return payouts;
    } catch (error: any) {
      console.error('Error fetching payout history:', error);
      throw new Error(error.message || 'Failed to fetch payout history');
    }
  }

  /**
   * Get specific payout status
   */
  async getPayoutStatus(payoutId: string): Promise<PayoutInfo> {
    try {
      console.log(`Fetching payout status for ${payoutId}...`);
      
      const response = await apiClient.get<ApiResponse<PayoutInfo>>(
        `${this.baseUrl}/payout/${payoutId}`
      );

      const payout = response.data;
      console.log('Payout status retrieved:', payout);
      
      return payout;
    } catch (error: any) {
      console.error('Error fetching payout status:', error);
      throw new Error(error.message || 'Failed to fetch payout status');
    }
  }

  /**
   * Validate bank details format
   */
  private validateBankDetails(bankDetails: BankDetails): void {
    const { accountHolderName, bankName, accountNumber, ifscCode } = bankDetails;

    if (!accountHolderName?.trim()) {
      throw new Error('Account holder name is required');
    }

    if (!bankName?.trim()) {
      throw new Error('Bank name is required');
    }

    if (!accountNumber?.trim()) {
      throw new Error('Account number is required');
    }

    if (!/^\d{9,18}$/.test(accountNumber)) {
      throw new Error('Account number must be 9-18 digits');
    }

    if (!ifscCode?.trim()) {
      throw new Error('IFSC code is required');
    }

    // IFSC code format validation: 4 alphabets + 0 + 6 alphanumeric
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(ifscCode.toUpperCase())) {
      throw new Error('Invalid IFSC code format (e.g., HDFC0001234)');
    }
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Format date and time
   */
  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'suspended':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Get payout status color for UI
   */
  getPayoutStatusColor(status: string): string {
    switch (status) {
      case 'processed':
        return 'text-green-600 bg-green-100';
      case 'processing':
        return 'text-blue-600 bg-blue-100';
      case 'queued':
        return 'text-yellow-600 bg-yellow-100';
      case 'pending':
        return 'text-orange-600 bg-orange-100';
      case 'failed':
      case 'cancelled':
      case 'reversed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Get status description
   */
  getStatusDescription(status: string): string {
    switch (status) {
      case 'pending':
        return 'Your application is under review. We\'ll notify you once it\'s processed.';
      case 'approved':
        return 'Congratulations! You can now start earning through referrals.';
      case 'rejected':
        return 'Your application was not approved. Please check the reason below.';
      case 'suspended':
        return 'Your ambassador account has been temporarily suspended.';
      default:
        return 'Unknown status';
    }
  }

  /**
   * Check if bank details are complete
   */
  isBankDetailsComplete(bankDetails?: BankDetails): boolean {
    if (!bankDetails) return false;
    
    return !!(
      bankDetails.accountHolderName?.trim() &&
      bankDetails.bankName?.trim() &&
      bankDetails.accountNumber?.trim() &&
      bankDetails.ifscCode?.trim()
    );
  }

  /**
   * Mask account number for display
   */
  maskAccountNumber(accountNumber: string): string {
    if (!accountNumber || accountNumber.length < 4) {
      return accountNumber;
    }
    
    // Check if already masked (contains asterisks or colons from encryption)
    if (accountNumber.includes('*') || accountNumber.includes(':')) {
      return accountNumber;
    }
    
    // Only show last 4 digits
    const lastFour = accountNumber.slice(-4);
    const maskedPart = '*'.repeat(Math.max(0, accountNumber.length - 4));
    return maskedPart + lastFour;
  }

  /**
   * Calculate completion percentage for application
   */
  getApplicationCompletionPercentage(application: AmbassadorApplication | null): number {
    if (!application) return 0;
    
    let completed = 0;
    const total = 3; // Total steps: application, bank details, approval
    
    // Step 1: Application submitted
    if (application._id) completed += 1;
    
    // Step 2: Bank details provided
    if (this.isBankDetailsComplete(application.bankDetails)) completed += 1;
    
    // Step 3: Approved
    if (application.status === 'approved') completed += 1;
    
    return Math.round((completed / total) * 100);
  }

  /**
   * Validate IFSC code format
   */
  validateIFSC(ifsc: string): boolean {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc);
  }

  /**
   * Validate account number format
   */
  validateAccountNumber(accountNumber: string): boolean {
    return /^\d{9,18}$/.test(accountNumber);
  }

  /**
   * Security note for bank details display
   */
  getBankDetailsSecurityNote(): string {
    return "Bank details are encrypted and securely stored. Only the last 4 digits of your account number are displayed for security.";
  }
}

// Export singleton instance
const ambassadorService = AmbassadorService.getInstance();
export default ambassadorService; 